import {
  getEmploymentInfoByUserId,
  updateEmploymentInfoByEmploymentInfoId,
} from "@/api/employment-info";
import EditButton from "@/components/buttons/EditButton";
import DatePicker from "@/components/form/DatePicker";
import Dropdown from "@/components/form/Dropdown";
import TextInput from "@/components/form/TextInput";
import {
  CreateEmploymentInfo,
  EmploymentInfo,
  EmploymentInfoDTO,
  EmploymentStatus,
} from "@/models/employment-info.model";
import React, { useEffect, useState } from "react";
import {
  employmentStatusOptions,
  employmentTypeOptions,
  passTypeOptions,
} from "../../onboard-page/EmploymentInfoForm";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { set } from "date-fns";
import { CustomDatePicker } from "@/components/form/CustomDatePicker";

interface Props {
  userId: number;
}
function EmploymentInfoTab({ userId }: Props) {
  const [employmentInfo, setEmploymentInfo] =
    React.useState<EmploymentInfoDTO | null>(null);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);
  const [employmentStatus, setEmploymentStatus] = useState<string>("");
  const [lastDayOfWork, setLastDayOfWork] = useState<Date | undefined>(); // can be null
  const [hireDate, setHireDate] = useState<Date | undefined>(new Date());
  const [confirmedDate, setConfirmedDate] = useState<Date | undefined>(
    new Date()
  );
  const [passType, setPassType] = useState<string>("");
  const [employmentType, setEmploymentType] = useState<string>("");
  const [designation, setDesignation] = useState<string>("");
  const [err, setErr] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const fetchEmploymentInfo = async () => {
      if (!userId) return;
      const employmentInfo = await getEmploymentInfoByUserId(userId);
      console.log(employmentInfo);
      setEmploymentInfo(employmentInfo);
      setDesignation(employmentInfo.designation);
      setEmploymentStatus(employmentInfo.employmentStatus);
      setLastDayOfWork(employmentInfo.lastDayOfWork ? new Date(employmentInfo.lastDayOfWork) : undefined);
      setHireDate(new Date(employmentInfo.hireDate));
      setConfirmedDate(
        employmentInfo.confirmedDate
          ? new Date(employmentInfo.confirmedDate)
          : new Date()
      );
      setPassType(employmentInfo.passType);
      setEmploymentType(employmentInfo.employmentType);
    };

    fetchEmploymentInfo();
  }, [userId]);

  const handleCancel = () => {
    setErr("");
    setIsReadOnly(true);
    if (!employmentInfo) return;
    setEmploymentStatus(employmentInfo.employmentStatus);
    setLastDayOfWork(employmentInfo.lastDayOfWork ? new Date(employmentInfo.lastDayOfWork) : undefined);
    setDesignation(employmentInfo.designation);
    setHireDate(new Date(employmentInfo.hireDate));
    setConfirmedDate(new Date(employmentInfo.confirmedDate || ""));
    setPassType(employmentInfo.passType);
    setEmploymentType(employmentInfo.employmentType);
  };

  const isNoChange = () => {
    const res =
      employmentInfo?.employmentStatus === employmentStatus &&
      employmentInfo?.hireDate === hireDate &&
      employmentInfo?.confirmedDate === confirmedDate &&
      employmentInfo?.passType === passType &&
      employmentInfo?.employmentType === employmentType &&
      employmentInfo.designation === designation &&
      employmentInfo?.lastDayOfWork === lastDayOfWork;
    return res;
  };


  const isValid = () => {
    if (
      designation.length === 0 ||
      passType.length === 0 ||
      employmentType.length === 0 ||
      hireDate === undefined
    ) {
      setErr("Please fill in all compulsory fields.");
      return false;
    }
    if (employmentStatus === EmploymentStatus.RESIGNED) {
      if (lastDayOfWork === undefined) {
        setErr("Last Day of Work is required for RESIGNED employees.");
        return false;
      };
    }
    if (employmentStatus === EmploymentStatus.ACTIVE) {
      if (lastDayOfWork !== undefined) {
        setErr("Last Day of Work should be empty for ACTIVE employees.");
        return false;
      }
    }
    setErr("");
    return true;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    if (!isValid() || !employmentInfo) {
      // set save btn as red
      setIsSubmitting(false);
      return;
    }

    try {
      const updatedEmploymentInfo: CreateEmploymentInfo = {
        designation,
        employmentStatus: employmentStatus as EmploymentStatus,
        hireDate: hireDate!,
        confirmedDate,
        passType,
        employmentType,
        lastDayOfWork,
      };

      const res = await updateEmploymentInfoByEmploymentInfoId(
        employmentInfo.id,
        updatedEmploymentInfo
      );
      if (res) {
        console.log(res);
        setEmploymentInfo(res);
        toast({
          title: "Employment info updated!",
          description: "Employment info has been successfully updated",
        });
        setIsReadOnly(true);
      } else {
        toast({
          title: "An error occurred",
          description: "Please try again",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "An error occurred",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-5">
      <div className="flex justify-between">
        <div>
          <div className="text-lg font-bold">Employment Details</div>
          <div className="w-full flex flex-col">
            <div
              className={` text-gray-500 text-sm transition-all duration-200 h-12`}
            >
              {isReadOnly
                ? "Details of the employee's employment."
                : "Edit information below."}
            </div>
          </div>
        </div>
        <EditButton
          handleEditing={() => setIsReadOnly(false)}
          isEditing={isReadOnly}
          handleCancel={handleCancel}
          isNoChange={isNoChange}
          handleSave={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>
      <form className="w-full h-full flex flex-col gap-6">
        <TextInput
          label="Designation *"
          value={designation}
          setValue={setDesignation}
          type="text"
          readOnly={isReadOnly}
        />
        <div className="flex w-full justify-between sm:gap-2 sm:flex-row flex-col gap-6">
          <CustomDatePicker
            date={hireDate}
            setDate={(date) => setHireDate(date)}
            label="Hire Date"
            disabled={isReadOnly}
          />
          <CustomDatePicker
            date={confirmedDate}
            setDate={setConfirmedDate}
            label="Confirmed Date"
            disabled={isReadOnly}
          />
        </div>
        <div className="flex w-full justify-between sm:gap-2 sm:flex-row flex-col gap-6">
          <Dropdown
            label="Employment Status"
            items={employmentStatusOptions}
            selectedString={employmentStatus}
            setSelectedString={setEmploymentStatus}
            isReadOnly={isReadOnly}
          />
          <CustomDatePicker
            date={lastDayOfWork}
            setDate={(date) => setLastDayOfWork(date)}
            label="Last Day of Work"
            disabled={isReadOnly}
            canClear={true}
          />
        </div>
        <Dropdown
          label="Pass Type *"
          items={passTypeOptions}
          selectedString={passType}
          setSelectedString={setPassType}
          isReadOnly={isReadOnly}
        />
        <Dropdown
          label="Employment Type *"
          items={employmentTypeOptions}
          selectedString={employmentType}
          setSelectedString={setEmploymentType}
          isReadOnly={isReadOnly}
        />
        {err && <div className="text-red-500 text-sm">{err}</div>}
      </form>
    </div>
  );
}

export default EmploymentInfoTab;
